import React from 'react';
import classNames from 'classnames';
import { formatPhoneNumber } from '../utils/validation';

interface PhoneFieldProps {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  autoComplete?: string;
  className?: string;
  helpText?: string;
}

export const PhoneField: React.FC<PhoneFieldProps> = ({
  id,
  name,
  label,
  value,
  onChange,
  onBlur,
  placeholder = 'e.g., 07123 456789 or +44 7123 456789',
  error,
  required = false,
  disabled = false,
  autoComplete = 'tel',
  className,
  helpText,
}) => {
  const hasError = Boolean(error);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    // Allow user to type freely, but store the raw value
    onChange(rawValue);
  };

  const handleBlur = () => {
    // Format the phone number on blur for better UX
    if (value.trim()) {
      const formatted = formatPhoneNumber(value);
      if (formatted !== value) {
        onChange(formatted);
      }
    }
    if (onBlur) {
      onBlur();
    }
  };

  return (
    <div className={classNames('space-y-1', className)}>
      <label
        htmlFor={id}
        className={classNames(
          'block text-sm font-medium',
          hasError ? 'text-error-700' : 'text-gray-700'
        )}
      >
        {label}
        {required && <span className="text-error-600 ml-1">*</span>}
      </label>
      
      <div className="relative">
        <input
          type="tel"
          id={id}
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          className={classNames(
            'form-input',
            {
              'border-error-300 focus:border-error-500 focus:ring-error-500': hasError,
              'opacity-50 cursor-not-allowed': disabled,
            }
          )}
          aria-invalid={hasError}
          aria-describedby={helpText ? `${id}-help` : undefined}
        />
        
        {/* Phone icon */}
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        </div>
      </div>

      {/* Help text and validation feedback */}
      <div className="mt-1 space-y-1">
        {helpText && (
          <p id={`${id}-help`} className="text-sm text-gray-500">
            {helpText}
          </p>
        )}
        
        {error && (
          <div className="validation-error">
            <div className="flex items-center">
              <svg className="h-4 w-4 text-error-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
